(() => {
  'use strict';
  if (window.tdmCredentialsInitialized) return;
  window.tdmCredentialsInitialized = true;

  const imageLayouts = new WeakMap();
  const imageListeners = new WeakSet();
  const observedFrames = new WeakSet();
  const initializedDialogs = new WeakSet();
  let returnFocus = null;
  let activeDialog = null;
  let requestedImageUrl = '';
  let beganOnBackdrop = false;

  const rotationFor = (value) => {
    const angle = Number(value);
    if (![0, 90, 180, -90, 270].includes(angle)) return 0;
    return ((angle % 360) + 360) % 360;
  };

  const fitImage = (image) => {
    const layout = imageLayouts.get(image);
    if (!layout || !image.naturalWidth || !image.naturalHeight) return;
    if (image.id === 'credentialimage') {
      image.setAttribute('width', image.naturalWidth);
      image.setAttribute('height', image.naturalHeight);
    }
    const { frame, rotation } = layout;
    if (!frame.isConnected) return;
    const style = window.getComputedStyle(frame);
    const width = frame.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
    const height = frame.clientHeight - (parseFloat(style.paddingTop) || 0) - (parseFloat(style.paddingBottom) || 0);
    if (width <= 0 || height <= 0) return;
    const quarterTurn = rotation === 90 || rotation === 270;
    const rotatedWidth = quarterTurn ? image.naturalHeight : image.naturalWidth;
    const rotatedHeight = quarterTurn ? image.naturalWidth : image.naturalHeight;
    const scale = Math.min(width / rotatedWidth, height / rotatedHeight);
    image.style.width = `${image.naturalWidth * scale}px`;
    image.style.height = `${image.naturalHeight * scale}px`;
    image.style.setProperty('--doc-rotation', `${rotation}deg`);
    image.setAttribute('data-credential-fitted', 'true');
  };

  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver((entries) => {
    entries.forEach(({ target }) => {
      const image = target.querySelector('img');
      if (image) fitImage(image);
    });
  }) : null;

  const bindImage = (image, frame, rotation) => {
    imageLayouts.set(image, { frame, rotation });
    image.style.setProperty('--doc-rotation', `${rotation}deg`);
    if (!imageListeners.has(image)) {
      image.addEventListener('load', () => fitImage(image));
      imageListeners.add(image);
    }
    if (resizeObserver && !observedFrames.has(frame)) {
      resizeObserver.observe(frame);
      observedFrames.add(frame);
    }
    if (image.complete && image.naturalWidth) fitImage(image);
  };

  const initializeThumbnails = (scope = document) => {
    const buttons = [...scope.querySelectorAll('[data-credential-open]')];
    if (scope.matches?.('[data-credential-open]')) buttons.unshift(scope);
    buttons.forEach((button) => {
      const frame = button.querySelector('.tdm-credential-photo-frame');
      const image = frame?.querySelector('img');
      if (!frame || !image) return;
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-controls', 'tdm-credential-dialog');
      bindImage(image, frame, rotationFor(button.dataset.credentialRotation));
    });
  };

  const getImageStatus = (stage) => {
    let status = stage.querySelector('.tdm-credential-image-status');
    if (!status) {
      status = document.createElement('p');
      status.className = 'tdm-credential-image-status';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      stage.append(status);
    }
    return status;
  };

  const setImageState = (stage, state) => {
    stage.dataset.loadState = state;
    const status = getImageStatus(stage);
    status.textContent = state === 'loading' ? 'Cargando fotografía…' : state === 'error' ? 'No pudimos cargar la fotografía aquí. Puedes abrir el archivo original con el enlace de abajo.' : '';
    status.hidden = state === 'ready';
  };

  const clickIsOutside = (event, dialog) => {
    const box = dialog.getBoundingClientRect();
    return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
  };

  const initializeDialog = (dialog) => {
    if (initializedDialogs.has(dialog)) return;
    initializedDialogs.add(dialog);
    const image = dialog.querySelector('#credentialimage');
    const stage = dialog.querySelector('.tdm-credential-dialog-stage');
    const heading = dialog.querySelector('#credentialtitle');
    if (heading) dialog.setAttribute('aria-labelledby', heading.id);
    dialog.querySelectorAll('[data-credential-close]').forEach((button) => {
      if (!button.getAttribute('aria-label')) button.setAttribute('aria-label', 'Cerrar fotografía del diploma');
      button.type = 'button';
    });
    if (image && stage) {
      image.addEventListener('load', () => {
        if (image.src !== requestedImageUrl) return;
        fitImage(image);
        setImageState(stage, 'ready');
      });
      image.addEventListener('error', () => {
        if (image.src === requestedImageUrl) setImageState(stage, 'error');
      });
    }
    dialog.addEventListener('pointerdown', (event) => {
      beganOnBackdrop = event.target === dialog && clickIsOutside(event, dialog);
    });
    dialog.addEventListener('click', (event) => {
      if (beganOnBackdrop && event.target === dialog && clickIsOutside(event, dialog)) dialog.close();
      beganOnBackdrop = false;
    });
    // Native <dialog> handles Escape and focus containment. Restore the opener
    // explicitly as well, including after an ordinary close-button click.
    dialog.addEventListener('close', () => {
      beganOnBackdrop = false;
      activeDialog = null;
      const target = returnFocus;
      returnFocus = null;
      if (target?.isConnected) queueMicrotask(() => target.focus({ preventScroll: true }));
    });
  };

  const openCredential = (button) => {
    const dialog = document.getElementById('tdm-credential-dialog');
    if (!dialog) return;
    const image = dialog.querySelector('#credentialimage');
    const heading = dialog.querySelector('#credentialtitle');
    const caption = dialog.querySelector('#credentialcaption');
    const original = dialog.querySelector('#credentialoriginal');
    const stage = dialog.querySelector('.tdm-credential-dialog-stage');
    if (!image || !heading || !caption || !original || !stage) return;
    let source;
    try {
      source = new URL(button.dataset.credentialImage || '', document.baseURI);
      if (!button.dataset.credentialImage || !['http:', 'https:'].includes(source.protocol)) return;
    } catch { return; }
    if (typeof dialog.showModal !== 'function') {
      window.open(source.href, '_blank', 'noopener,noreferrer');
      return;
    }
    initializeDialog(dialog);
    returnFocus = button;
    activeDialog = dialog;
    requestedImageUrl = source.href;
    const article = button.closest('.tdm-credential');
    const title = button.dataset.credentialTitle || article?.querySelector('.tdm-credential-copy h3')?.textContent?.trim() || 'Diploma y formación';
    const institution = article?.querySelector('.tdm-credential-copy .tdm-eyebrow')?.textContent?.trim();
    const detail = article?.querySelector('.tdm-credential-copy p:not(.tdm-eyebrow)')?.textContent?.trim();
    heading.textContent = title;
    image.alt = `Fotografía original: ${title}`;
    const thumbnail = button.querySelector('.tdm-credential-photo-frame img');
    image.setAttribute('width', thumbnail?.getAttribute('width') || '1280');
    image.setAttribute('height', thumbnail?.getAttribute('height') || '960');
    caption.textContent = [institution, detail].filter(Boolean).join(' · ') || 'Fotografía original del documento. Se conserva su contenido y solo se ajusta la orientación para facilitar la lectura.';
    original.href = source.href;
    original.target = '_blank';
    original.rel = 'noopener noreferrer';
    original.textContent = 'Ver fotografía original';
    setImageState(stage, 'loading');
    bindImage(image, stage, rotationFor(button.dataset.credentialRotation));
    image.src = source.href;
    if (!dialog.open) dialog.showModal();
    const closeButton = dialog.querySelector('[data-credential-close]');
    closeButton?.focus({ preventScroll: true });
    requestAnimationFrame(() => {
      if (!dialog.open || image.src !== requestedImageUrl) return;
      if (image.complete && image.naturalWidth) {
        fitImage(image);
        setImageState(stage, 'ready');
      }
    });
  };

  document.addEventListener('click', (event) => {
    const close = event.target.closest?.('[data-credential-close]');
    if (close) {
      const dialog = close.closest('.tdm-credential-dialog');
      if (dialog?.open) { event.preventDefault(); dialog.close(); }
      return;
    }
    const button = event.target.closest?.('[data-credential-open]');
    if (!button) return;
    event.preventDefault();
    openCredential(button);
  });

  document.addEventListener('shopify:section:load', (event) => initializeThumbnails(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.tdm-credential-photo-frame,.tdm-credential-dialog-stage').forEach((frame) => {
      resizeObserver?.unobserve(frame);
      observedFrames.delete(frame);
    });
    if (activeDialog && event.target.contains(activeDialog)) {
      if (activeDialog.open) activeDialog.close();
      activeDialog = null;
    }
  });
  if (!resizeObserver) window.addEventListener('resize', () => {
    document.querySelectorAll('.tdm-credential-photo-frame>img,.tdm-credential-dialog[open] #credentialimage').forEach(fitImage);
  }, { passive: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeThumbnails(), { once: true });
  else initializeThumbnails();
})();
